import React, { useMemo } from 'react';
import { formatNumber, formatDifference, formatRank, formatPercentile } from '../../utils/formatting';
import { getValueColor } from '../../utils/coloring';
import { getStateAbbreviation } from '../../constants/locations';
import './Tables.css';

const LocationNameTotalsTable = ({ selectedLocation, selectedCrime, selectedSource, compareMode }) => {
  const current = selectedLocation?.data?.current;
  const locationType = selectedLocation.info?.type?.charAt(0).toUpperCase() + selectedLocation.info?.type?.slice(1);
  const isNation = selectedLocation.info?.type?.toLowerCase() === 'nation';
  const isCounty = selectedLocation.info?.type?.toLowerCase() === 'county';
  const isCity = selectedLocation.info?.type?.toLowerCase() === 'city';
  const isCountyOrCity = isCounty || isCity;
  
  const stateAbbr = useMemo(() => {
    if (isCountyOrCity && selectedLocation?.info?.name?.includes(',')) {
      const stateName = selectedLocation.info.name.split(',')[1]?.trim();
      return getStateAbbreviation(stateName);
    }
    return getStateAbbreviation(selectedLocation);
  }, [isCountyOrCity, selectedLocation]);

  if (selectedLocation?.loading || !selectedLocation?.data?.current || selectedLocation?.info?.name?.startsWith('All ')) {
    return null;
  }

  const hasCrimeData = [
    'all_crime_count', 'violent_crime_count', 'property_crime_count',
    'murder_count', 'rape_count', 'robbery_count', 'aggravated_assault_count',
    'burglary_count', 'larceny_theft_count', 'motor_vehicle_theft_count'
  ].some(field => 
    current[field] !== null && 
    current[field] !== undefined && 
    current[field] !== 'N/A'
  );
  
  if (!hasCrimeData) {
    return <div className="loading">No Data Available</div>;
  }

  const transformData = () => {
    const isNCVS = selectedSource === 'BJS_NCVS';

    const getComparisonFields = (prefix) => {
      const rankField = isCountyOrCity
        ? compareMode === 'state'
          ? `${prefix}_state_count_rank`
          : `${prefix}_national_count_rank`
        : `${prefix}_count_rank`;
      
      const percentileField = isCountyOrCity
        ? compareMode === 'state'
          ? `${prefix}_state_count_percentile`
          : `${prefix}_national_count_percentile`
        : `${prefix}_count_percentile`;
      
      return {
        nationalAvg: current[`${prefix}_national_count_avg`],
        nationalDiff: current[`${prefix}_national_count_avg_diff`],
        nationalDiffPct: current[`${prefix}_national_count_avg_diff_pct`],
        stateAvg: isCountyOrCity ? current[`${prefix}_state_count_avg`] : null,
        stateDiff: isCountyOrCity ? current[`${prefix}_state_count_avg_diff`] : null,
        stateDiffPct: isCountyOrCity ? current[`${prefix}_state_count_avg_diff_pct`] : null,
        rank: current[rankField],
        percentile: current[percentileField]
      };
    };

    const allCrimes = [
      {
        crimeType: 'All Crime',
        level: 0,
        currentTotal: current.all_crime_count,
        ...getComparisonFields('all_crime')
      },
      {
        crimeType: 'Violent Crime',
        level: 1,
        currentTotal: current.violent_crime_count,
        ...getComparisonFields('violent_crime')
      },
      {
        crimeType: 'Murder',
        level: 2,
        currentTotal: current.murder_count,
        ...getComparisonFields('murder')
      },
      {
        crimeType: 'Rape',
        level: 2,
        currentTotal: current.rape_count,
        ...getComparisonFields('rape')
      },
      {
        crimeType: 'Robbery',
        level: 2,
        currentTotal: current.robbery_count,
        ...getComparisonFields('robbery')
      },
      {
        crimeType: 'Aggravated Assault',
        level: 2,
        currentTotal: current.aggravated_assault_count,
        ...getComparisonFields('aggravated_assault')
      },
      ...(isNCVS ? [{
        crimeType: 'Simple Assault',
        level: 2,
        currentTotal: current.simple_assault_count,
      }] : []),
      {
        crimeType: 'Property Crime',
        level: 1,
        currentTotal: current.property_crime_count,
        ...getComparisonFields('property_crime')
      },
      {
        crimeType: 'Burglary',
        level: 2,
        currentTotal: current.burglary_count,
        ...getComparisonFields('burglary')
      },
      ...(isNCVS ? [{
        crimeType: 'Trespassing',
        level: 2,
        currentTotal: current.trespassing_count,
      }] : []),
      {
        crimeType: 'Larceny/Theft',
        level: 2,
        currentTotal: current.larceny_theft_count,
        ...getComparisonFields('larceny_theft')
      },
      {
        crimeType: 'Motor Vehicle Theft',
        level: 2,
        currentTotal: current.motor_vehicle_theft_count,
        ...getComparisonFields('motor_vehicle_theft')
      }
    ];

    return allCrimes;
  };

  const renderRow = (item) => {
    const isSelected = item.crimeType.toLowerCase().replace(/[\s/]/g, '_') === selectedCrime?.toLowerCase();

    const avgValue = compareMode === 'state' && isCountyOrCity && item.stateAvg !== null 
      ? item.stateAvg 
      : item.nationalAvg;
      
    const diffValue = compareMode === 'state' && isCountyOrCity && item.stateDiff !== null 
      ? item.stateDiff 
      : item.nationalDiff;
      
    const diffPctValue = compareMode === 'state' && isCountyOrCity && item.stateDiffPct !== null 
      ? item.stateDiffPct 
      : item.nationalDiffPct;

    return (
      <tr key={item.crimeType} className={`level-${item.level}${isSelected ? ' selected-row' : ''}`}>
        <td className="crime-type-cell">
          <div className="crime-type-content">
            <span>{item.crimeType}</span>
          </div>
        </td>
        <td className={`number-cell ${isNation ? 'nation' : ''} bold`}>{formatNumber(item.currentTotal)}</td>
        {!isNation && (
          <>
            <td className="number-cell">{formatNumber(avgValue)}</td>
            <td className="number-cell" style={{ color: getValueColor(diffValue) }}>
              {formatDifference(diffValue)}
            </td>
            <td className="number-cell" style={{ color: getValueColor(diffPctValue) }}>
              {formatDifference(diffPctValue, true)}
            </td>
            <td className="rank-cell">{formatRank(item.rank)}</td>
            <td className="rank-cell">{formatPercentile(item.percentile)}</td>
          </>
        )}
      </tr>
    );
  };

  const data = transformData();

  return (
    <div className="main-table-wrapper">
      <div className="main-table-top">
      {!isNation ? (
        <>
          {isCountyOrCity ? (
            <>
              Total {compareMode === 'state' ? stateAbbr : 'US'} {isCounty ? 'Counties' : 'Cities'}:&nbsp;
              <strong>
                {formatNumber(
                  compareMode === 'state' 
                    ? (isCounty ? current.total_counties_state : current.total_cities_state) || current.total_locations 
                    : (isCounty ? current.total_counties_national : current.total_cities_national) || current.total_locations
                )}
              </strong>     
            </>
          ) : (
            <>
              Total {locationType}s:&nbsp;<strong>{formatNumber(current.total_locations)}</strong>     
            </>
          )}
        </>
      ) : (
        <>
          Results for the entire nation
        </>
      )}
      </div>
      <div className="main-table-scroll-container location-name-table">
        <table className={`main-table location-name-table ${isNation ? 'nation-view' : ''}`}>
          <thead>
            <tr>
              <th>Type of Crime</th>
              <th>
                {isNation ? (
                  <div className="single-line-header">
                    {selectedSource === 'BJS_NCVS' ? 'Total Victimizations' : 'Total Crime'}
                  </div>
                ) : (
                  <div className="stacked-header">
                    <div>Total</div>
                    <div>Crime</div>
                  </div>
                )}
              </th>
              {!isNation && (
                <>
                  <th>
                    <div className="stacked-header">
                      <div>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType}</div>
                      <div>Average</div>
                    </div>
                  </th>
                  <th>
                    <div className="stacked-header">
                      <div>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType} Avg</div>
                      <div>Difference</div>
                    </div>
                  </th>
                  <th>
                    <div className="stacked-header">
                      <div>% {isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType} Avg</div>
                      <div>Difference</div>
                    </div>
                  </th>
                  <th>
                    <div className="stacked-header">
                      <div>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType}</div>
                      <div>Rank</div>
                    </div>
                  </th>
                  <th>
                    <div className="stacked-header">
                      <div>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType}</div>
                      <div>Percentile</div>
                    </div>
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {data.map(item => renderRow(item))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LocationNameTotalsTable;